import { useApolloClient, useQuery } from '@apollo/client';
import { SortModel } from '@components/DataTable/types';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_JOB_OFFER } from '@gql/jobs';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import { ScoringBatchStatusCard } from '@modules/scoring/components/ScoringBatchStatusCard';
import { ScoringResultsTable } from '@modules/scoring/components/ScoringResultsTable';
import { useGetScoringBatch } from '@modules/scoring/hooks/useGetScoringBatch';
import { useTriggerScoring } from '@modules/scoring/hooks/useTriggerScoring';
import { ScoringStatus, TriggerScoringResult } from '@modules/scoring/types';
import TalentMediaDialog from '@modules/talent-media/TalentMediaDialog';
import { useTalentSearch } from '@modules/talent-search/hooks/useTalentSearch';
import TalentSearchFilter from '@modules/talent-search/TalentSearchFilter';
import { ITalentSearchFilter } from '@modules/talent-search/types';
import TalentTableOrganisms from '@modules/talents/TalentTableOrganisms';
import { useTalentOperations } from '@modules/talents/context/TalentOperationsContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const warningLabels: Record<string, string> = {
    missing_personi_id: 'offre sans identifiant Personi',
    missing_trimoji_resume_id: 'talent sans CV Trimoji',
    missing_targets: 'aucun talent sélectionné',
};

const JobOfferScoringModule = () => {
    const { slug } = useParams<{ slug: string }>();
    const apolloClient = useApolloClient();
    const { showSnackbar } = useSnackbar();
    const { selectedTalents, clearSelection } = useTalentOperations();
    const { triggerScoring, loading: scoringLoading } = useTriggerScoring();

    const [viewMode] = useState<'list'>('list');
    const [lastTriggeredBatch, setLastTriggeredBatch] = useState<TriggerScoringResult | null>(null);
    const [isBatchDialogOpen, setIsBatchDialogOpen] = useState(false);
    const [mediaDialog, setMediaDialog] = useState<{
        open: boolean;
        talentId: string;
        defaultTab: 'scoring' | 'documents' | 'notes' | 'files';
        score?: number | null;
        scoringStatus?: ScoringStatus;
        scoreExplainer?: string;
    }>({
        open: false,
        talentId: '',
        defaultTab: 'scoring',
    });

    const batchIdForDialog = isBatchDialogOpen ? (lastTriggeredBatch?.batchId ?? null) : null;

    const {
        batch: dialogBatch,
        loading: dialogBatchLoading,
        error: dialogBatchError,
        refetch: refetchDialogBatch,
    } = useGetScoringBatch(batchIdForDialog);

    const { data: jobOfferData } = useQuery(GET_JOB_OFFER, {
        variables: { jobNumber: slug, showInactive: true },
        skip: !slug,
    });

    const jobOffer = jobOfferData?.getJobOffer;

    const {
        filters,
        setFilters,
        updateFilter,
        sortBy,
        setSortBy,
        talents,
        totalDocs,
        loading,
        isLoadingMore,
        hasMore,
        loadMore,
        error,
        refetch,
        availableFilters,
        page,
        pageSize,
        handlePageChange,
        handlePageSizeChange,
    } = useTalentSearch({ context: 'GLOBAL', jobOfferId: jobOffer?._id });

    useEffect(() => {
        apolloClient.cache.evict({ fieldName: 'searchTalents' });
        apolloClient.cache.evict({ fieldName: 'searchTalentFilters' });
        apolloClient.cache.gc();
    }, [apolloClient, slug]);

    useEffect(() => {
        clearSelection();
        return () => {
            clearSelection();
        };
    }, [clearSelection, slug]);

    const talentRows = useMemo(
        () => talents.map((item: any) => item.talent || item),
        [talents],
    );

    const scoringResultsByTalentId = useMemo(
        () => talentRows.reduce<Record<string, { score?: number | null; status: 'pending' | 'in_progress' | 'done' | 'error'; explainer?: string }>>((acc, row: any) => {
            if (typeof row?.jobOfferScoring === 'number') {
                acc[row._id] = {
                    score: row.jobOfferScoring,
                    status: row.jobOfferScoringStatus || 'done',
                    explainer: row.jobOfferScoringExplainer ?? undefined,
                };
            }

            return acc;
        }, {}),
        [talentRows],
    );

    const handleOpenScoringDialog = useCallback((
        talentId: string,
        data: { score?: number | null; status: 'pending' | 'in_progress' | 'done' | 'error'; explainer?: string },
    ) => {
        setMediaDialog({
            open: true,
            talentId,
            defaultTab: 'scoring',
            score: data.score,
            scoringStatus: data.status as ScoringStatus,
            scoreExplainer: data.explainer,
        });
    }, []);

    const sortModel = useMemo<SortModel[]>(() => {
        const fieldMap: Record<string, string> = {
            NAME: 'name',
            EXPERIENCE: 'experience',
            RATING: 'rating',
            CREATED_AT: 'createdAt',
            CITY: 'city',
            JOB_OFFER_SCORING: 'scoring',
        };

        const columnId = fieldMap[sortBy.field];

        if (!columnId) {
            return [];
        }

        return [{ field: columnId, sort: sortBy.order === 'ASC' ? 'asc' : 'desc' }];
    }, [sortBy]);

    const onQueryChange = useCallback((value: string) => {
        updateFilter('query', value);
    }, [updateFilter]);

    const onFilterChange = useCallback((newFilter: ITalentSearchFilter) => {
        setFilters(newFilter);
    }, [setFilters]);

    const handleTableSortChange = useCallback((model: SortModel[]) => {
        const columnToField: Record<string, string> = {
            name: 'NAME',
            rating: 'RATING',
            city: 'CITY',
            experience: 'EXPERIENCE',
            createdAt: 'CREATED_AT',
            scoring: 'JOB_OFFER_SCORING',
        };

        const active = model[0];

        if (!active?.sort) {
            setSortBy({ field: 'CREATED_AT', order: 'DESC' });
            return;
        }

        const field = columnToField[active.field];

        if (field) {
            setSortBy({ field, order: active.sort === 'asc' ? 'ASC' : 'DESC' });
        }
    }, [setSortBy]);

    const validationWarningSummary = useMemo(() => {
        if (!lastTriggeredBatch?.validationWarnings?.length) {
            return null;
        }

        return lastTriggeredBatch.validationWarnings
            .map((warning) => warningLabels[warning.reason] || warning.reason)
            .join(', ');
    }, [lastTriggeredBatch]);

    const handleTriggerScoring = useCallback(async () => {
        if (!jobOffer?._id) {
            showSnackbar('Impossible de lancer le scoring sans offre valide.', 'warning');
            return;
        }

        if (selectedTalents.length === 0) {
            showSnackbar('Sélectionnez au moins un talent à scorer.', 'warning');
            return;
        }

        const result = await triggerScoring({
            mode: 'many_to_one',
            jobOfferId: jobOffer._id,
            targets: selectedTalents.map(({ _id }) => ({ talentId: _id })),
        });

        if (!result) {
            showSnackbar('Erreur lors du déclenchement du scoring.', 'error');
            return;
        }

        setLastTriggeredBatch(result);
        setIsBatchDialogOpen(true);

        if (result.totalQueued === 0) {
            showSnackbar('Aucun scoring n\'a été mis en file.', 'warning');
            return;
        }

        clearSelection();
        showSnackbar(`${result.totalQueued} scoring(s) lancé(s).`, 'success');
    }, [clearSelection, jobOffer, selectedTalents, showSnackbar, triggerScoring]);

    const handleTriggerAllToOneScoring = useCallback(async () => {
        if (!jobOffer?._id) {
            showSnackbar('Impossible de lancer le scoring sans offre valide.', 'warning');
            return;
        }

        const result = await triggerScoring({
            mode: 'all_to_one',
            jobOfferId: jobOffer._id,
        });

        if (!result) {
            showSnackbar('Erreur lors du déclenchement du scoring global.', 'error');
            return;
        }

        setLastTriggeredBatch(result);
        setIsBatchDialogOpen(true);

        if (result.totalQueued === 0) {
            showSnackbar('Aucun scoring n\'a été mis en file.', 'warning');
            return;
        }

        clearSelection();
        showSnackbar(`${result.totalQueued} scoring(s) all_to_one lancé(s).`, 'success');
    }, [clearSelection, jobOffer, showSnackbar, triggerScoring]);

    if (error) {
        return (
            <Alert severity="error">
                Erreur lors du chargement des talents : {error.message}
            </Alert>
        );
    }

    return (
        <Stack sx={{ flex: 1, gap: 3, width: '100%' }}>
            {lastTriggeredBatch && (
                <Alert
                    severity={lastTriggeredBatch.totalQueued > 0 ? 'success' : 'warning'}
                    action={
                        lastTriggeredBatch.totalQueued > 0 ? (
                            <Button
                                onClick={() => setIsBatchDialogOpen(true)}
                                color="inherit"
                                size="small"
                            >
                                Voir la progression
                            </Button>
                        ) : undefined
                    }
                >
                    {lastTriggeredBatch.totalQueued > 0
                        ? `${lastTriggeredBatch.totalQueued} talent(s) ajouté(s) au batch ${lastTriggeredBatch.batchId}.`
                        : 'Aucun talent sélectionné n\'a pu être mis en file.'}
                    {validationWarningSummary ? ` Avertissements : ${validationWarningSummary}.` : ''}
                </Alert>
            )}

            {jobOffer && !jobOffer.personiId && (
                <Alert severity="warning">
                    Cette offre n'a pas d'identifiant Personi — le scoring ne pourra pas être lancé.
                </Alert>
            )}

            <TalentSearchFilter
                context="JOB_OFFER"
                jobOfferIdExists={Boolean(jobOffer?._id)}
                totalDocs={totalDocs}
                onQueryChange={onQueryChange}
                onFilterChange={onFilterChange}
                filter={filters}
                availableFilters={availableFilters}
                endAdornment={
                    <Stack direction="row" spacing={1.5} sx={{ marginLeft: 'auto', flexShrink: 0 }}>
                    <Button
                        color="secondary"
                        size="large"
                        onClick={handleTriggerAllToOneScoring}
                        disabled={!jobOffer?._id || scoringLoading}
                        startIcon={<i className="fi-rr-sparkles" style={{ fontSize: '14px', marginRight: '8px' }} />}
                    >
                        {scoringLoading ? 'Scoring...' : 'Scorer tous les talents'}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleTriggerScoring}
                        startIcon={<i className="fi-rr-sparkles" style={{ fontSize: '14px', marginRight: '8px' }} />}
                        disabled={!jobOffer?._id || selectedTalents.length === 0 || scoringLoading}
                    >
                        {scoringLoading ? 'Scoring...' : 'Scorer la sélection'}
                    </Button>
                </Stack>
                }
            />

            <TalentTableOrganisms
                viewMode={viewMode}
                data={talentRows}
                loading={loading}
                scoringResultsByTalentId={scoringResultsByTalentId}
                showScoringColumn
                onOpenScoringDialog={handleOpenScoringDialog}
                handleDownload={() => undefined}
                sortModel={sortModel}
                onSortChange={handleTableSortChange}
                isLoadingMore={isLoadingMore}
                onScrollEnd={hasMore ? loadMore : undefined}
                showFloatingActionBar={false}
                onReuploadSuccess={refetch}
                pagination={{
                    currentPage: page + 1,
                    pageSize,
                    totalRows: totalDocs,
                    onPageChange: handlePageChange,
                    onPageSizeChange: handlePageSizeChange,
                }}
            />

            <Dialog
                open={isBatchDialogOpen}
                onClose={() => setIsBatchDialogOpen(false)}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>
                    Progression du batch
                    {lastTriggeredBatch?.batchId ? ` ${lastTriggeredBatch.batchId}` : ''}
                </DialogTitle>
                <DialogContent>
                    {!lastTriggeredBatch?.batchId ? (
                        <Alert severity="warning">Aucun batch sélectionné.</Alert>
                    ) : dialogBatchLoading && !dialogBatch ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : dialogBatchError ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            Erreur lors du chargement du batch : {dialogBatchError.message}
                        </Alert>
                    ) : dialogBatch ? (
                        <Box sx={{ py: 1 }}>
                            <ScoringBatchStatusCard batch={dialogBatch} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                                Résultats ({dialogBatch.total})
                            </Typography>
                            <ScoringResultsTable
                                results={dialogBatch.results}
                                loading={dialogBatchLoading}
                            />
                        </Box>
                    ) : (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Batch introuvable.
                        </Alert>
                    )}

                    {dialogBatch && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button onClick={() => refetchDialogBatch()} size="small" variant="text">
                                Rafraîchir
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <TalentMediaDialog
                isOpen={mediaDialog.open}
                onClose={() => setMediaDialog(prev => ({ ...prev, open: false }))}
                talentId={mediaDialog.talentId}
                defaultTab={mediaDialog.defaultTab}
                score={mediaDialog.score}
                scoringStatus={mediaDialog.scoringStatus}
                scoreExplainer={mediaDialog.scoreExplainer}
                jobOfferId={jobOffer?._id}
                jobOfferTitle={jobOffer?.title}
                jobOfferNumber={jobOffer?.jobNumber}
            />
        </Stack>
    );
};

export default JobOfferScoringModule;
